import { ref } from 'vue';

import LogRocket from 'logrocket';
import { defineStore } from 'pinia';

import { getInformationForCustomerPortal } from '@core/helpers/Customer/customer-helper';

import { useBusinessStore } from '@core/stores/customer/BusinessStore';
import { useCustomerApplicationStore } from '@core/stores/customer/CustomerApplicationStore';
import { useCustomerStore } from '@core/stores/customer/CustomerStore';

import Application from '@core/classes/Applications/Application';
import CustomerDealPayment from '@core/classes/Customer/CustomerDealPayment';
import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import CustomerVehicle from '@core/classes/Customer/CustomerVehicle';
import DealForCustomerPortal from '@core/classes/Customer/DealForCustomerPortal';
import { PlaidCustomerIncomeData } from '@core/classes/Plaid/PlaidCustomerIncomeData';
import util from '@core/services/util';

export const useUserStore = defineStore('user', () => {
	const customerData = ref<CustomerRecord>(null);
	const businessData = ref<CustomerRecord>(null);
	const customerAppData = ref<Application>(null);
	const dealPayments = ref<CustomerDealPayment[]>(null);
	const plaidIncomeData = ref<PlaidCustomerIncomeData>(null);
	const customerVehiclesData = ref<CustomerVehicle[]>(null);
	const dealsByVehicleData = ref<Record<string, DealForCustomerPortal>>(null);

	const isInitialLoading = ref(false);

	/** Action to set data in both UserStore and CustomerStore */
	const setCustomerData = (customer: CustomerRecord) => {
		customerData.value = customer;
		const customerStore = useCustomerStore();
		customerStore.customer = customer;
	};

	/** Action to set data in both UserStore and BusinessStore */
	const setBusinessData = (business: CustomerRecord) => {
		businessData.value = business;
		const businessStore = useBusinessStore();
		businessStore.business = business;
	};

	/** Action to set data in both UserStore and CustomerAppStore */
	const setCustomerAppData = (customerApp: Application) => {
		customerAppData.value = customerApp;
		const customerAppStore = useCustomerApplicationStore();
		customerAppStore.customerApplication = customerApp;
	};

	/** Action to set data in both UserStore and CustomerStore */
	const setCustomerVehicles = (customerVehicles: CustomerVehicle[]) => {
		customerVehiclesData.value = customerVehicles;
		const customerStore = useCustomerStore();
		customerStore.vehicles = customerVehicles;
	};

	/** Action to set data in both UserStore and CustomerStore */
	const setDealsByVehicle = (dealsByVehicle: Record<string, DealForCustomerPortal>) => {
		const entries = Object.entries(dealsByVehicle ?? {});
		// Backend sends null for vehicles with no deal. `new DealForCustomerPortal(null)` still yields a truthy object
		const instantiatedEntries = entries.filter(([, deal]) => deal != null).map(([vehicleId, deal]) => [vehicleId, new DealForCustomerPortal(deal)]);

		dealsByVehicleData.value = Object.fromEntries(instantiatedEntries);
		const customerStore = useCustomerStore();
		customerStore.dealsByVehicle = dealsByVehicleData.value;
	};

	/** Action to set data in both UserStore and CustomerStore */
	const setDealPaymentsData = (payments: CustomerDealPayment[]) => {
		dealPayments.value = payments.map(p => new CustomerDealPayment(p));
		const customerStore = useCustomerStore();
		customerStore.setDealPayments(payments);
	};

	/** Action to set data in both UserStore and CustomerStore */
	const setPlaidIncomeData = (incomeData: PlaidCustomerIncomeData) => {
		plaidIncomeData.value = new PlaidCustomerIncomeData(incomeData);
		const customerStore = useCustomerStore();
		customerStore.setPlaidIncomeData(incomeData);
	};

	const initializeCustomerData = async (customerId: string) => {
		if (customerId == null) return;
		if (customerData.value) {
			isInitialLoading.value = false;
			return;
		}

		try {
			isInitialLoading.value = true;
			const response = await getInformationForCustomerPortal(customerId);
			if (!response) return;

			LogRocket.log('Customer Portal response', response as any);

			setCustomerData(response.customer);
			setCustomerAppData(response.customerApplication);
			setBusinessData(response.businessCustomer);
			setCustomerVehicles(response.customerVehicles);
			setDealsByVehicle(response.dealsByVehicle);
			setDealPaymentsData(response.dealPayments);
			setPlaidIncomeData(response.plaidIncomeData);

			util.logRocketTrack(`Customer_Portal_Entered`, {
				customerId: response.customer?.id,
				customerCell: response.customer?.personalInfo?.cell,
				customerEmail: response.customer?.personalInfo?.email,
				customerDob: response.customer?.personalInfo?.dob,
				applicationId: response.customerApplication?.id,
				businessCustomerId: response.businessCustomer?.id,
			});
		} catch (error) {
			LogRocket.error('Error loading customer data', error);
		} finally {
			isInitialLoading.value = false;
		}
	};

	return {
		customerData,
		businessData,
		customerAppData,
		plaidIncomeData,
		dealPayments,
		isInitialLoading,
		customerVehiclesData,
		dealsByVehicleData,
		setCustomerData,
		setBusinessData,
		setCustomerAppData,
		setDealPaymentsData,
		setPlaidIncomeData,
		initializeCustomerData,
	};
});
